import React, { FC } from 'react';
import { Paper, TextField, InputProps as MuiInputProps } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import Downshift, { Actions } from 'downshift';

import { ISuggestProps, SuggestionTypeObject } from './types';
import Suggestion from './Suggestion';

interface IProps extends ISuggestProps, WithStyles<typeof styles> {
  suggestions: SuggestionTypeObject[];
}

const styles = (theme: Theme) =>
  createStyles({
    container: {
      flexGrow: 1,
      position: 'relative',
    },
    paper: {
      position: 'absolute',
      zIndex: 1,
      left: 0,
      right: 0,
    },
    inputRoot: {
      flexWrap: 'wrap',
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.grey[400]}`,
      borderRadius: 4,
    },
    inputInput: {
      width: 'auto',
      flexGrow: 1,
    },
  });

const SuggestComponent: FC<IProps> = props => {
  const {
    classes,
    inputValue,
    type,
    placeholder,
    onChange,
    onInputChange,
    suggestions,
    TextFieldProps,
  } = props;

  function handleChange(selection: SuggestionTypeObject, options: Actions<SuggestionTypeObject>) {
    options.clearSelection(() => {
      if (selection) {
        onChange(selection);
      }
    });
  }

  function retrieveInputProps(): MuiInputProps {
    return {
      placeholder,
      classes: {
        root: classes.inputRoot,
        input: classes.inputInput,
      },
      onKeyDown: event => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      },
    };
  }

  return (
    <Downshift
      onChange={handleChange}
      onInputValueChange={onInputChange}
      inputValue={inputValue}
      itemToString={item => (item && item.title) || ''}>
      {downshiftProps => {
        const {
          getInputProps,
          getItemProps,
          highlightedIndex,
          getMenuProps,
          isOpen,
        } = downshiftProps;
        const hasSuggestions = isOpen && !!suggestions.length;

        return (
          <div className={classes.container}>
            <TextField InputProps={getInputProps(retrieveInputProps())} {...TextFieldProps} />
            <div {...getMenuProps()}>
              {hasSuggestions && (
                <Paper className={classes.paper} square>
                  {suggestions.map((suggestion, index) => (
                    <Suggestion
                      key={suggestion.id}
                      type={type}
                      suggestionProps={{
                        index,
                        suggestion,
                        highlightedIndex,
                        itemProps: getItemProps({ item: suggestion }),
                      }}
                    />
                  ))}
                </Paper>
              )}
            </div>
          </div>
        );
      }}
    </Downshift>
  );
};

SuggestComponent.defaultProps = {
  TextFieldProps: {
    fullWidth: true,
    margin: 'normal',
  },
};

export default withStyles(styles)(SuggestComponent);
